import { Component, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { IncentiveNamePipe } from './ft-finalist-incentives.component';

/**
 * Native port of the AngularJS `ftIncentiveSelector` directive: the incentive dropdown
 * inside each goal row of the friends-goals editor (`.ft-dropdown.goal-incentives`).
 * On select it syncs `goal.incentiveUUID` + `goal.languages` in place and defaults to the
 * first incentive when unset; the toggle label is the `incentiveName`-resolved SafeHtml.
 * Derivations run imperatively in the input setters since the parent mutates `goal` in place.
 */
@Component({
    selector: 'ft-incentive-selector-ng',
    imports: [IncentiveNamePipe],
    providers: [IncentiveNamePipe],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-incentive-selector.component.html',
    host: {
        '(document:click)': 'onDocumentClick()',
    }
})
export class FtIncentiveSelectorComponent {
    /** The goal slot ({ incentiveUUID, languages }); mutated in place on select. */
    readonly goal = input<any>();

    /** The warm-lead-tagged property incentives `{ item: [] }` the dropdown lists. */
    readonly incentives = input<any>();

    /** The selected language (v1 `culture`, the `model.language` OBJECT) for the toggle name. */
    readonly culture = input<any>();

    /** v1 `availableIncentives | filter:{isActive:true}` — the rendered menu list. */
    protected readonly menuIncentives = signal<any[]>([]);
    /** v1 `scope.goalName = $sce.trustAsHtml(incentiveName(current, culture))`. */
    protected readonly goalName = signal<SafeHtml>('');
    protected readonly active = signal(false);

    private readonly parse = inject(ParseService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly incentiveNamePipe = inject(IncentiveNamePipe);

    constructor() {
        effect(() => this.recompute());
    }

    /** v1 `setIncentive` — write the picked incentive's id + languages onto the goal (in-place). */
    protected setIncentive(incentive: any): void {
        const goal = this.goal();
        if (!goal) return;
        goal.incentiveUUID = incentive.id;
        goal.languages = incentive.languages;
        this.recompute();
    }

    /** v1 `.goal-incentives` `ft-click-class toggle-class="active"` (toggle on click). */
    protected toggle(event: Event): void {
        event.stopPropagation();
        this.active.update((active) => !active);
    }

    /** v1 `ft-click-class` document-click close. */
    protected onDocumentClick(): void {
        this.active.set(false);
    }

    /**
     * v1's two deep `$watch`es collapsed into one imperative derivation:
     *  - `$watch("incentives")`: availableIncentives = filter(arrayOrDefault(incentives),
     *    i => parse.bool(i.isActive) && i.languages); the menu adds the `filter:{isActive:true}`
     *    substring pass (default comparator).
     *  - `$watch("goal", true)`: resolve the current incentive by `goal.incentiveUUID` in
     *    `incentives.item`; if none, default to `item[0]` and sync incentiveUUID + languages;
     *    goalName = trustAsHtml(incentiveName(current, culture)).
     */
    private recompute(): void {
        const goal = this.goal();
        const incentives = this.incentives();
        const culture = this.culture();
        const all = this.parse.arrayOrDefault(incentives);
        const available = all.filter((i: any) => this.parse.bool(i.isActive) && i.languages);
        this.menuIncentives.set(
            available.filter((i: any) => String(i.isActive).toLowerCase().indexOf('true') !== -1),
        );

        if (goal && incentives) {
            const items = incentives.item || [];
            let incentive = items.find((i: any) => i.id == goal.incentiveUUID);
            if (!incentive) {
                incentive = items[0];
                goal.incentiveUUID = incentive && incentive.id;
                if (incentive) goal.languages = incentive.languages;
            }
            this.goalName.set(
                this.sanitizer.bypassSecurityTrustHtml(this.incentiveNamePipe.transform(incentive, culture)),
            );
        }
    }
}
